import { CustomFieldAPI } from '@business/api/customField';
import { SettingsAPI } from '@business/api/settings';
import { MBSettingsRequest } from '@business/entities/marketing';
import { NylasRequest } from '@business/entities/nylas/NylasResponse';
import { FieldId, SettingFieldResponse } from '@business/entities/settingFields';
import { SettingsResponse } from '@business/entities/settings';
import { EscalateRequest } from '@business/entities/tenant';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { SettingsConfig } from '@core/utils/config';
import { Session } from '@modules/auth/session';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap, tap } from 'rxjs/operators';
import { AutoTagsSettingResponse, OrderSettingResponse, TMSettingResponse } from './../../entities/settings/SettingsResponse';
import { DealSettingsFormData } from '@modules/settings/SettingsPage/Components/DealsTabs/Tabs/DealSettingsTab';
import { CommissionConfigFormData } from '@modules/settings/SettingsPage/Components/CommissionSettingsTab/CommissionConfigTab';
import { ProposalSettingsFormData } from '@modules/settings/SettingsPage/Components/BillingTab/Tabs/ProposalSettingsTab';
import { TMProjectSettingsFormData } from '@modules/settings/SettingsPage/Components/ProjectsTabs/Tabs/ProjectSettingsTab/ProjectSettingsTab';
import { TMSupportSettingsFormData } from '@modules/settings/SettingsPage/Components/SupportTab/SupportSettingsTab/SupportSettingsTab';
import { SettingsFormData } from '@modules/settings/SettingsPage/Components/StatusTab/StatusSettingsTab/StatusSettingsTab';
import { ClockinUsersSettingsFormData } from '@modules/settings/SettingsPage/Components/ClockinTab/ClockinUserSettingsTab';
import { TMSettingsFormData } from '@modules/settings/SettingsPage/Components/TimeMaterialsTab/Tabs/FlagStatusTab/FlagStatusTab';
import { AffiliateSettingsFormData } from '@modules/settings/SettingsPage/Components/AffiliateTab/AffiliateTab';
import { SchedularSettingsFormData } from '@modules/settings/SettingsPage/Components/SchedulerTab/SchedulerSettingsTab/SchedulerSettingsTab';

export class SettingsBloc extends BaseBloc {
  onInit() {
    if (Session.isAuthenticated) {
      this.fetchSettings().subscribe();
    }
  }

  fetchSettings = () => {
    return SettingsAPI.fetchSettings().pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ error: requestMessage('fetch_companies_error') }),
    );
  };

  createCustomField = (field: Partial<SettingFieldResponse>) => {
    return CustomFieldAPI.createCustomField(field).pipe(
      tap(() => this.fetchSettings().subscribe()),
      handleMessage({ type: requestMessage('create_field') }),
    );
  };

  deleteCustomField = (fieldId: FieldId) => {
    return showDeleteConfirmation('Delete Field', 'Do you want to delete this field? This action cannot be undone').pipe(
      switchMap(() => CustomFieldAPI.deleteCustomField(fieldId)),
      tap(() => this.fetchSettings().subscribe()),
      handleMessage({ type: requestMessage('delete_field') }),
    );
  };

  updateCustomField = (id: FieldId, field: Partial<SettingFieldResponse>) => {
    return CustomFieldAPI.updateCustomField(id, field).pipe(
      tap(() => this.fetchSettings().subscribe()),
      handleMessage({ type: requestMessage('update_field') }),
    );
  };

  sortCustomField = (ids: FieldId[]) => {
    return CustomFieldAPI.sortCustomField(ids).pipe(
      tap(() => this.fetchSettings().subscribe()),
      handleMessage({ error: requestMessage('fetch_general_error') }),
    );
  };

  updateNylasConfig = (data: NylasRequest) => {
    return SettingsAPI.updateNylasConfig(data).pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ error: requestMessage('connect_nylas_error') }),
    );
  };

  updateEscalate = (data: EscalateRequest) => {
    return SettingsAPI.updateEscalate(data).pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ error: requestMessage('esc_account_error'), success: requestMessage('esc_account_saved') }),
    );
  };
  updateMBSettings = (data: MBSettingsRequest) => {
    return SettingsAPI.updateMBSettings(data).pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ error: requestMessage('esc_account_error'), success: requestMessage('esc_account_saved') }),
    );
  };
  updateOrderSettings = (data: Partial<OrderSettingResponse>) => {
    return SettingsAPI.updateOrderSettings(data).pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ type: requestMessage('settings_updated') }),
    );
  };

  updateDealSettings = (data: DealSettingsFormData) => {
    return SettingsAPI.updateDealSettings(data).pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ type: requestMessage('settings_updated') }),
    );
  };

  updateTMSettings = (data: TMSettingsFormData) => {
    return SettingsAPI.updateTMSettings(data).pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ type: requestMessage('settings_updated') }),
    );
  };

  updateBillingSettings = (data: Partial<SettingsResponse>) => {
    return SettingsAPI.updateBillingSettings(data).pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ type: requestMessage('settings_updated') }),
    );
  };

  updateUsersSettings = (data: Partial<SettingsResponse>) => {
    return SettingsAPI.updateUsersSettings(data).pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ type: requestMessage('settings_updated') }),
    );
  };

  updateClockinUsersSettings = (data: ClockinUsersSettingsFormData) => {
    return SettingsAPI.updateClockinUsersSettings(data).pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ type: requestMessage('settings_updated') }),
    );
  };

  updateInstructorsSettings = (data: Partial<SettingsResponse>) => {
    return SettingsAPI.updateInstructorsSettings(data).pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ type: requestMessage('settings_updated') }),
    );
  };

  saveCommissionSettings = (data: CommissionConfigFormData) => {
    return SettingsAPI.saveCommissionSettings(data).pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ type: requestMessage('settings_updated') }),
    );
  };

  saveAffiliateSettings = (affiliate: AffiliateSettingsFormData) => {
    return SettingsAPI.updateAffilate({ affiliate }).pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ type: requestMessage('settings_updated') }),
    );
  };

  updateProposalSettings = (data: ProposalSettingsFormData) => {
    return SettingsAPI.updateProposalSettings(data).pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ type: requestMessage('settings_updated') }),
    );
  };

  getQBSubCompare = () => {
    return SettingsAPI.getQBSubCompare().pipe(handleMessage({ error: requestMessage('sub_compare') }));
  };

  updateTmSettings = (data: Partial<TMSettingResponse>) => {
    return SettingsAPI.updateTmSettings(data).pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ type: requestMessage('settings_updated') }),
    );
  };

  updateProjectSettings = (data: Partial<TMProjectSettingsFormData>) => {
    return SettingsAPI.updateProjectSettings(data).pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ type: requestMessage('settings_updated') }),
    );
  };

  updateSupportSettings = (data: Partial<TMSupportSettingsFormData>) => {
    return SettingsAPI.updateSupportSettings(data).pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ type: requestMessage('settings_updated') }),
    );
  };

  updateTagsSettings = (data: Partial<AutoTagsSettingResponse>) => {
    return SettingsAPI.updateTagsSettings(data).pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ type: requestMessage('settings_updated') }),
    );
  };

  updateStatusSettings = (data: Partial<SettingsFormData>) => {
    return SettingsAPI.updateStatusSettings(data).pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ type: requestMessage('settings_updated') }),
    );
  };
  updateSchedularSettings = (data: Partial<SchedularSettingsFormData>) => {
    return SettingsAPI.updateSchedularSettings(data).pipe(
      tap(settings => SettingsConfig.set(settings)),
      handleMessage({ type: requestMessage('settings_updated') }),
    );
  };
}
